/**
 * Frontend Logger - отправляет логи на бэкенд для централизованного хранения
 */

import { API_URL } from '../config';

class FrontendLogger {
  constructor() {
    this.buffer = [];
    this.bufferSize = 10;  // Отправляем после накопления 10 логов
    this.flushInterval = 30000;  // Или каждые 30 секунд
    this.sessionId = this.generateSessionId();
    
    // Периодический flush
    setInterval(() => this.flush(), this.flushInterval);
    
    // Flush при закрытии страницы
    window.addEventListener('beforeunload', () => this.flush(true));
  }
  
  generateSessionId() {
    return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }
  
  getToken() {
    return localStorage.getItem('token');
  }
  
  /**
   * Логирование UI событий
   */
  logUI(message, details = {}) {
    this.log('ui', 'info', message, details);
  }
  
  /**
   * Логирование API вызовов
   */
  logAPI(message, details = {}) {
    this.log('api', 'info', message, details);
  }
  
  /**
   * Логирование ошибок
   */
  logError(message, error = null, details = {}) {
    const errorDetails = {
      ...details,
      error_message: error?.message,
      error_stack: error?.stack,
    };
    this.log('error', 'error', message, errorDetails);
  }
  
  /**
   * Логирование производительности
   */
  logPerformance(message, details = {}) {
    this.log('perf', 'info', message, details);
  }
  
  /**
   * Базовый метод логирования
   */
  log(category, level, message, details = {}) {
    const entry = {
      category,
      level,
      message,
      details: {
        ...details,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      },
      session_id: this.sessionId
    };
    
    this.buffer.push(entry);
    
    // Также выводим в консоль для отладки
    const logMethod = level === 'error' ? console.error : (level === 'warning' ? console.warn : console.log);
    logMethod(`[${category.toUpperCase()}] ${message}`, details);
    
    // Flush если буфер полон
    if (this.buffer.length >= this.bufferSize) {
      this.flush();
    }
  }
  
  /**
   * Отправка накопленных логов на сервер
   */
  async flush(sync = false) {
    if (this.buffer.length === 0) return;
    
    const token = this.getToken();
    if (!token) {
      // Не авторизован - очищаем буфер без отправки
      this.buffer = [];
      return;
    }
    
    const events = [...this.buffer];
    this.buffer = [];
    
    const payload = {
      events,
      session_id: this.sessionId
    };
    
    try {
      if (sync && navigator.sendBeacon) {
        // Синхронная отправка при закрытии страницы
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(`${API_URL}/api/logs/batch`, blob);
      } else {
        await fetch(`${API_URL}/api/logs/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }
    } catch (error) {
      // Логирование ошибки отправки - возвращаем в буфер
      console.error('Failed to send logs:', error);
      this.buffer = [...events, ...this.buffer].slice(-100);  // Не больше 100 записей
    }
  }
  
  /**
   * Логирование навигации
   */
  logNavigation(from, to) {
    this.logUI(`Navigation: ${from} -> ${to}`, { from, to });
  }
  
  /**
   * Логирование клика
   */
  logClick(element, details = {}) {
    this.logUI(`Click: ${element}`, { element, ...details });
  }
  
  /**
   * Логирование API запроса
   */
  logApiRequest(method, url, status, duration, error = null) {
    const level = status >= 500 ? 'error' : (status >= 400 ? 'warning' : 'info');
    const message = `${method} ${url} - ${status} (${duration}ms)`;
    
    this.log('api', level, message, {
      method,
      url,
      status,
      duration,
      error: error?.message
    });
  }
  
  /**
   * Логирование загрузки страницы
   */
  logPageLoad(pageName, loadTime) {
    this.logPerformance(`Page loaded: ${pageName}`, {
      page: pageName,
      load_time_ms: loadTime
    });
  }
}

// Singleton instance
const frontendLogger = new FrontendLogger();

export default frontendLogger;

// Удобные функции для импорта
export const logUI = (message, details) => frontendLogger.logUI(message, details);
export const logAPI = (message, details) => frontendLogger.logAPI(message, details);
export const logError = (message, error, details) => frontendLogger.logError(message, error, details);
export const logPerformance = (message, details) => frontendLogger.logPerformance(message, details);
export const logNavigation = (from, to) => frontendLogger.logNavigation(from, to);
export const logClick = (element, details) => frontendLogger.logClick(element, details);
export const logApiRequest = (method, url, status, duration, error) => frontendLogger.logApiRequest(method, url, status, duration, error);
export const logPageLoad = (pageName, loadTime) => frontendLogger.logPageLoad(pageName, loadTime);
